import { useEffect, useState } from 'react';

/**
 * Le mois de travail d'un écran, qui SURVIT à la navigation.
 *
 * Constat de recette (Lot 7, point 10) : on calcule juillet dans « Tickets », on
 * passe au calendrier d'équipe, on revient — et l'écran est retombé sur le mois
 * courant. Le travail d'un mois se fait en plusieurs passages entre les écrans ;
 * remettre le mois à chaque retour, c'est faire refaire le chemin à la main, et
 * risquer de valider le mauvais mois sans s'en apercevoir.
 *
 * `sessionStorage` plutôt que `localStorage` : le mois choisi vaut pour la
 * session de travail en cours, pas pour toujours. Rouvrir Gertrude demain doit
 * proposer le mois courant, pas celui d'un travail terminé.
 *
 * Chaque écran a SA clé : les onglets sont indépendants, on peut préparer la
 * paie de juillet en regardant le calendrier de septembre.
 */
export function useMoisDeSession(cle: string, defaut: string) {
  const cleComplete = `conges.mois.${cle}`;

  const [mois, setMois] = useState<string>(() => {
    // Un navigateur peut refuser l'accès au stockage (navigation privée,
    // réglages) : l'écran doit s'ouvrir quand même, sur le mois courant.
    try {
      const garde = sessionStorage.getItem(cleComplete);
      return garde && /^\d{4}-\d{2}$/.test(garde) ? garde : defaut;
    } catch {
      return defaut;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(cleComplete, mois);
    } catch {
      // Tant pis : le mois ne survivra pas au changement d'écran, l'écran, lui,
      // fonctionne.
    }
  }, [cleComplete, mois]);

  return [mois, setMois] as const;
}

/** Le mois courant, au format « AAAA-MM ». */
export function moisCourant(): string {
  return new Date().toISOString().slice(0, 7);
}
